import type { DetailedPosition } from '@my-roadtrip/types'
import { calculateDistance } from '@my-roadtrip/utils'

export const DISTANCE_FILTER_DEFAULTS = {
  maxAccuracyMeters: 75, // discard very noisy samples
  minMoveMeters: 6, // ignore tiny drift when stopped
  maxSpeedKmh: 240, // cap to realistic road/track speeds
  minTimeDeltaSeconds: 1,
  maxJumpMeters: 2500, // ignore teleports over ~2.5 km within a few seconds
  enableAltitude3d: true
} as const

export type DistanceComputationResult = {
  deltaMeters: number
  accepted: boolean
  reason?: string
  derivedSpeedKmh?: number
}

export function normalizePositionInput(
  raw: Partial<DetailedPosition> | undefined,
  fallbackTimestamp: Date
): DetailedPosition | null {
  if (!raw) return null

  const lat = Number((raw as any)?.lat)
  const lng = Number((raw as any)?.lng)

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null
  // Avoid the classic 0,0 GPS bug
  if (Math.abs(lat) < 1e-6 && Math.abs(lng) < 1e-6) return null

  let timestamp =
    raw.timestamp instanceof Date
      ? raw.timestamp
      : raw.timestamp
        ? new Date(raw.timestamp as any)
        : fallbackTimestamp

  // Clamp future timestamps to now to avoid time-travel artefacts
  const now = Date.now()
  if (timestamp.getTime() > now + 2 * 60 * 1000) {
    timestamp = new Date(now)
  }

  const position: DetailedPosition = {
    lat,
    lng,
    timestamp
  }

  if (raw.accuracy !== undefined && Number.isFinite(Number(raw.accuracy))) {
    position.accuracy = Math.max(0, Number(raw.accuracy))
  }
  if (raw.altitude !== undefined && Number.isFinite(Number(raw.altitude))) {
    position.altitude = Number(raw.altitude)
  }
  if (raw.heading !== undefined && Number.isFinite(Number(raw.heading))) {
    position.heading = Number(raw.heading)
  }
  if (raw.speed !== undefined && Number.isFinite(Number(raw.speed))) {
    position.speed = Math.max(0, Number(raw.speed))
  }

  return position
}

export function computeDistanceDelta(
  previous: DetailedPosition | null,
  next: DetailedPosition,
  overrides: Partial<typeof DISTANCE_FILTER_DEFAULTS> = {}
): DistanceComputationResult {
  const config = { ...DISTANCE_FILTER_DEFAULTS, ...overrides }

  // No previous point → cannot compute delta yet
  if (!previous) {
    return { deltaMeters: 0, accepted: false, reason: 'first_point', derivedSpeedKmh: 0 }
  }

  const surfaceDistance = calculateDistance(previous, next)

  const deltaAltitude =
    config.enableAltitude3d && Number.isFinite(previous.altitude ?? NaN) && Number.isFinite(next.altitude ?? NaN)
      ? Math.abs((next.altitude as number) - (previous.altitude as number))
      : 0

  const deltaMeters = deltaAltitude > 0 ? Math.hypot(surfaceDistance, deltaAltitude) : surfaceDistance
  const timeDeltaSeconds = Math.max(
    config.minTimeDeltaSeconds,
    (next.timestamp.getTime() - previous.timestamp.getTime()) / 1000
  )
  const derivedSpeedKmh = deltaMeters > 0 ? (deltaMeters / timeDeltaSeconds) * 3.6 : 0

  if (next.accuracy !== undefined && next.accuracy > config.maxAccuracyMeters) {
    return { deltaMeters: 0, accepted: false, reason: 'low_accuracy', derivedSpeedKmh }
  }

  const accuracyGate = Math.max(
    config.minMoveMeters,
    ((next.accuracy ?? 0) + (previous.accuracy ?? 0)) / 2
  )
  if (deltaMeters < accuracyGate) {
    return { deltaMeters: 0, accepted: false, reason: 'gps_drift', derivedSpeedKmh }
  }

  if (derivedSpeedKmh > config.maxSpeedKmh) {
    return { deltaMeters: 0, accepted: false, reason: 'unrealistic_speed', derivedSpeedKmh }
  }

  if (config.maxJumpMeters && deltaMeters > config.maxJumpMeters && timeDeltaSeconds <= 30) {
    return { deltaMeters: 0, accepted: false, reason: 'teleport', derivedSpeedKmh }
  }

  return { deltaMeters, accepted: true, derivedSpeedKmh }
}
